import React, { useMemo, useState } from 'react';
import { addMonths, eachDayOfInterval, endOfMonth, format, isSameDay, isWeekend, max as maxDate, min as minDate, parseISO, startOfMonth } from 'date-fns';
import { CalendarDaysIcon, ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { timeOffEntries } from '../../data/timeOff';
import type { EntryTone, Navigate, PtoRequest } from '../../types/hr';
import { displayName, getEmployee } from '../../utils/employees';
import { TODAY, formatDays, formatRange } from '../../utils/format';
import { overlaps, policyName } from '../../utils/timeOff';
import { Avatar } from '../ui/Avatar';
import { Card } from '../ui/Card';
import { EmptyState } from '../ui/EmptyState';
interface WhosOutCalendarProps {
  requests: PtoRequest[];
  onNavigate: Navigate;
}
interface CalendarItem {
  id: string;
  employeeId: string;
  start: string;
  end: string;
  days: number;
  label: string;
  tone: EntryTone;
  pending: boolean;
}
const BAR_TONES: Record<EntryTone, string> = {
  blue: 'bg-azure text-white',
  teal: 'bg-deep text-white',
  navy: 'bg-navy text-white',
  orange: 'bg-flame text-white'
};
export function WhosOutCalendar({
  requests,
  onNavigate
}: WhosOutCalendarProps) {
  const [month, setMonth] = useState(startOfMonth(TODAY));
  const monthEnd = endOfMonth(month);
  const days = eachDayOfInterval({
    start: month,
    end: monthEnd
  });
  const items: CalendarItem[] = useMemo(() => [...timeOffEntries.map(e => ({
    id: e.id,
    employeeId: e.employeeId,
    start: e.start,
    end: e.end,
    days: e.days,
    label: e.policyLabel,
    tone: e.tone,
    pending: false
  })), ...requests.filter(r => r.status === 'pending' || r.status === 'approved').map(r => ({
    id: r.id,
    employeeId: r.employeeId,
    start: r.start,
    end: r.end,
    days: r.days,
    label: policyName(r.policyId),
    tone: (r.policyId === 'newhire' ? 'navy' : 'blue') as EntryTone,
    pending: r.status === 'pending'
  }))], [requests]);
  const rows = useMemo(() => {
    const inMonth = items.filter(i => overlaps(i, month, monthEnd));
    const byEmployee = new Map<string, CalendarItem[]>();
    inMonth.forEach(i => byEmployee.set(i.employeeId, [...(byEmployee.get(i.employeeId) ?? []), i]));
    return Array.from(byEmployee.entries()).sort((a, b) => a[1][0].start.localeCompare(b[1][0].start));
  }, [items, month, monthEnd]);
  const grid = {
    gridTemplateColumns: `repeat(${days.length}, minmax(0, 1fr))`
  };
  return <Card title={format(month, 'MMMM yyyy')} subtitle={`${rows.length} people with time off`} icon={CalendarDaysIcon} action={<div className="flex items-center gap-1" data-id="element-1075" data-mp="1mf0ltfqqji5x">
          <button type="button" onClick={() => setMonth(startOfMonth(TODAY))} className="rounded-lg px-2.5 py-1.5 text-xs font-medium text-azure hover:bg-azure/10" data-id="element-1076" data-mp="1mce5orps3uc3">
            Today
          </button>
          <button type="button" onClick={() => setMonth(addMonths(month, -1))} aria-label="Previous month" className="rounded-lg p-1.5 text-navy hover:bg-canvas" data-id="element-1077" data-mp="1cie3axlvt372">
            <ChevronLeftIcon className="h-4 w-4" data-id="element-1078" data-mp="3t1oft3tgtj0g" />
          </button>
          <button type="button" onClick={() => setMonth(addMonths(month, 1))} aria-label="Next month" className="rounded-lg p-1.5 text-navy hover:bg-canvas" data-id="element-1079" data-mp="1wpwf5encvvs">
            <ChevronRightIcon className="h-4 w-4" data-id="element-1080" data-mp="5x8lwst2hput" />
          </button>
        </div>} bodyClassName="pb-5" data-id="element-1074" data-mp="1vduif642g8jh">
      {rows.length === 0 ? <div className="px-6" data-id="element-1081" data-mp="3abgm1cbno4wx">
          <EmptyState icon={CalendarDaysIcon} title="No time off this month" data-id="element-1082" data-mp="3acrlx11zcsir" />
        </div> : <div className="overflow-x-auto" data-id="element-1083" data-mp="3ecjzxta57qvi">
          <div className="min-w-[960px] px-6" data-id="element-1084" data-mp="3j4biphn24sb3">
            <div className="flex border-b border-slate-100 pb-2" data-id="element-1085" data-mp="3j64nssi0v4gz">
              <div className="w-52 shrink-0" data-id="element-1086" data-mp="2mngwgfq2ykjx" />
              <div className="grid flex-1" style={grid} data-id="element-1087" data-mp="2mkkxco2hxptw">
                {days.map(d => {
              const isToday = isSameDay(d, TODAY);
              return <div key={d.toISOString()} className="flex flex-col items-center gap-0.5" data-id="element-1088" data-mp="27k5o1sou00it">
                      <span className="text-[9px] font-medium uppercase text-mutedlight" data-id="element-1089" data-mp="22lvpd83wehsg">{format(d, 'EEEEE')}</span>
                      <span className={`flex h-6 w-6 items-center justify-center rounded-full text-[11px] font-medium ${isToday ? 'bg-flame text-white' : isWeekend(d) ? 'text-slate-300' : 'text-slate-600'}`} data-id="element-1090" data-mp="2haryz9lls2v6">
                        {format(d, 'd')}
                      </span>
                    </div>;
            })}
              </div>
            </div>

            {rows.map(([employeeId, entries]) => {
          const emp = getEmployee(employeeId);
          if (!emp) return null;
          return <div key={employeeId} className="group flex items-center border-b border-slate-50 last:border-0" data-id="element-1091" data-mp="10vgzyc49btn5">
                  <button type="button" onClick={() => onNavigate({
              page: 'profile',
              employeeId,
              tab: 'timeoff'
            })} className="flex w-52 shrink-0 items-center gap-2.5 py-2.5 pr-3 text-left" data-id="element-1092" data-mp="3ciaphrgdo151">
                    <Avatar name={displayName(emp)} seed={emp.id} size="sm" data-id="element-1093" data-mp="37rua41szy5g1" />
                    <span className="min-w-0" data-id="element-1094" data-mp="37pr8slme8pw0">
                      <span className="block truncate text-sm font-medium text-navy group-hover:text-azure" data-id="element-1095" data-mp="2trt8ul8tkw01">{displayName(emp)}</span>
                      <span className="block truncate text-[11px] text-muted" data-id="element-1096" data-mp="2xv7o39oxp8cf">{emp.department}</span>
                    </span>
                  </button>
                  <div className="relative h-11 flex-1" data-id="element-1097" data-mp="2osgbh6kbo6j7">
                    <div className="absolute inset-0 grid" style={grid} aria-hidden="true" data-id="element-1098" data-mp="2ov2in53awk0w">
                      {days.map(d => <div key={d.toISOString()} className={`border-l border-slate-100/70 ${isWeekend(d) ? 'bg-canvas' : ''} ${isSameDay(d, TODAY) ? 'bg-flame/[0.06]' : ''}`} data-id="element-1099" data-mp="n1d3kp1th62c" />)}
                    </div>
                    <div className="absolute inset-0 grid items-center" style={grid} data-id="element-1100" data-mp="blqfmy364a9c">
                      {entries.map(entry => {
                  const s = maxDate([parseISO(entry.start), month]).getDate();
                  const e = minDate([parseISO(entry.end), monthEnd]).getDate();
                  return <div key={entry.id} title={`${entry.label} · ${formatRange(entry.start, entry.end)}${entry.pending ? ' (pending)' : ''}`} style={{
                    gridColumn: `${s} / ${e + 1}`,
                    gridRow: 1
                  }} className={`mx-0.5 flex h-7 items-center overflow-hidden rounded-lg px-2 text-[11px] font-medium shadow-sm ${entry.pending ? 'border-2 border-dashed border-flame bg-flame/10 text-[#c25a00]' : BAR_TONES[entry.tone]}`} data-id="element-1101" data-mp="zccousows0kx">
                            <span className="truncate" data-id="element-1102" data-mp="1un0wa2i5ruff">{entry.pending ? 'Pending' : formatDays(entry.days)}</span>
                          </div>;
                })}
                    </div>
                  </div>
                </div>;
        })}

            <div className="mt-4 flex flex-wrap items-center gap-5 text-xs text-muted" data-id="element-1103" data-mp="29g4dg8ru865m">
              <Legend className="bg-azure" label="Regular PTO" data-id="element-1104" data-mp="2dhzd78u4ebha" />
              <Legend className="bg-navy" label="New Hire PTO" data-id="element-1105" data-mp="2ia916jbod1gx" />
              <Legend className="bg-deep" label="Leave of Absence" data-id="element-1106" data-mp="2n9lunmx1x6sh" />
              <Legend className="border-2 border-dashed border-flame bg-flame/10" label="Pending request" data-id="element-1107" data-mp="2n8txw8rpx30m" />
            </div>
          </div>
        </div>}
    </Card>;
}
function Legend({
  className,
  label
}: {
  className: string;
  label: string;
}) {
  return <span className="inline-flex items-center gap-2" data-id="element-1108" data-mp="3ihot102yyug1">
      <span className={`h-3 w-5 rounded ${className}`} aria-hidden="true" data-id="element-1109" data-mp="3ikkngeflz0zq" />
      {label}
    </span>;
}